/**
 * HomePersonaToggle — segmented control for switching between the Sales, CSR,
 * Ops and Finance home dashboards.
 *
 * A separate component from SalesOpsToggle so that component's two-way
 * contract (Sales | Ops, used in Jobs and Estimates) remains unchanged.
 *
 * Each tab navigates INTO another feature, so each is gated on its own route
 * being reachable — otherwise a tab is a button to the not-found page. That
 * was live in production the whole time `csr_home` and `production_ops` were
 * held back, because sales home is live and rendered all three
 * unconditionally. `production_ops` is still held back, so the gate is load
 * bearing today, not defensive.
 */

import { isRouteEnabled } from "@shared-core/routing/routes";

import {
  HOME_PERSONAS,
  type HomePersona,
} from "@shared-core/domain/homePersona";

export function HomePersonaToggle({
  current,
  onSelect,
  ariaLabel = "Home view",
}: {
  current: HomePersona;
  onSelect: (persona: HomePersona) => void;
  ariaLabel?: string;
}) {
  // The current persona's own tab stays regardless: the page is rendering, so
  // its route resolved. Filtering it out would leave the control with nothing
  // marked selected.
  const reachable = HOME_PERSONAS.filter(
    (tab) => tab.persona === current || isRouteEnabled(tab.route),
  );

  // One tab is not a choice — it is a label that looks clickable.
  if (reachable.length < 2) return null;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex rounded-md border border-border overflow-hidden"
    >
      {reachable.map((tab, i) => {
        const active = current === tab.persona;
        return (
          <button
            key={tab.persona}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (!active) onSelect(tab.persona);
            }}
            className={`px-3 py-1.5 text-sm transition-colors ${
              i > 0 ? "border-l border-border" : ""
            } ${
              active
                ? "bg-primary text-white"
                : "bg-surface text-foreground hover:bg-surface-hover"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
